import type { JSX } from 'react';
import { NavLink } from 'react-router';

import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../content/categories.js';
import { getComponentsByCategory } from '../../content/component-registry.js';

export interface DocsSidebarProps {
  mobileOpen?: boolean;
  onNavigate?: () => void;
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    'block rounded-md px-3 py-1.5 text-sm no-underline transition-colors',
    isActive
      ? 'bg-[var(--color-docs-accent-soft)] font-medium text-[var(--color-docs-accent)]'
      : 'text-[var(--color-docs-muted)] hover:bg-[var(--color-docs-code-bg)] hover:text-[var(--color-docs-ink)]',
  ].join(' ');
}

/** Left navigation drawer grouped by category. */
export function DocsSidebar({ mobileOpen = false, onNavigate }: DocsSidebarProps): JSX.Element {
  const byCategory = getComponentsByCategory();

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-20 w-64 transform border-r border-[var(--color-docs-border)] bg-[var(--color-docs-surface)] pt-14 transition-transform lg:static lg:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ].join(' ')}
    >
      <nav className="h-full overflow-x-hidden overflow-y-auto px-4 py-4" onClick={onNavigate}>
        <section className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-docs-muted)]">
            Getting started
          </p>
          <ul className="space-y-0.5">
            <li>
              <NavLink to="/getting-started" className={navLinkClass} end>
                Overview
              </NavLink>
            </li>
            <li>
              <NavLink to="/components" className={navLinkClass}>
                All components
              </NavLink>
            </li>
          </ul>
        </section>

        <section className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-docs-muted)]">
            Foundations
          </p>
          <ul className="space-y-0.5">
            <li>
              <NavLink to="/foundations/brand" className={navLinkClass}>
                Brand
              </NavLink>
            </li>
            <li>
              <NavLink to="/foundations/colors" className={navLinkClass}>
                Colors
              </NavLink>
            </li>
            <li>
              <NavLink to="/foundations/elevation" className={navLinkClass}>
                Elevation
              </NavLink>
            </li>
            <li>
              <NavLink to="/foundations/spacing" className={navLinkClass}>
                Spacing
              </NavLink>
            </li>
          </ul>
        </section>

        <section className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-docs-muted)]">
            Typography
          </p>
          <ul className="space-y-0.5">
            {(byCategory.get('typography') ?? [])
              .filter((entry) => entry.status === 'stable')
              .map((entry) => (
                <li key={entry.slug}>
                  <NavLink to={`/components/${entry.slug}`} className={navLinkClass}>
                    {entry.name}
                  </NavLink>
                </li>
              ))}
          </ul>
        </section>

        <section className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-docs-muted)]">
            Components
          </p>
          {CATEGORY_ORDER.filter((category) => category !== 'typography').map((category) => {
            const entries = byCategory.get(category) ?? [];
            const stable = entries.filter((e) => e.status === 'stable');
            if (stable.length === 0) return null;
            return (
              <div key={category} className="mb-4">
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-docs-muted)]">
                  {CATEGORY_LABELS[category]}
                </p>
                <ul className="space-y-0.5">
                  {stable.map((entry) => (
                    <li key={entry.slug}>
                      <NavLink to={`/components/${entry.slug}`} className={navLinkClass}>
                        {entry.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      </nav>
    </aside>
  );
}
